/**
 * Classifies and parses stable entity references stored inside the single
 * `string` link field used by pickers and slate/widget configs (e.g. a Navbar
 * `MenuItem.url`). Building the tokens lives in `./build-link.ts`.
 *
 * ## Why
 *
 * A "Select space" picker used to persist the space's derived path
 * (`space.relativeUrl`, e.g. `/feed`) directly. When the space's slug later
 * changed, the stored path no longer matched and the link 404'd. Encoding the
 * stable `spaceId` lets renderers resolve the *current* path at read time, so
 * the link survives slug changes. The path is kept alongside as a fallback for
 * correct first paint (SSR) and for when the entity can't be resolved.
 *
 * ## Format
 *
 * `space://<spaceId>:<fallback>`
 *
 * The value is parsed by splitting on the FIRST `:` after the `space://`
 * scheme. Space ids are nanoid `[0-9A-Za-z]` (see tribe-api
 * `libs/typeorm-util/src/util/generate-entity-id.ts`), so they never contain
 * `:` or `/`; everything after that first colon is the opaque fallback path,
 * which MAY itself contain `:` and `/`.
 *
 * All variations:
 * - Space link:            `space://vVqXtuy:/feed`
 * - Nested path:           `space://vVqXtuy:/my-collection/announcements`
 * - Empty fallback (edge): `space://vVqXtuy:`
 * - Raw external URL:      `https://example.com/docs` (not a resolvable link)
 * - Raw internal path:     `/custom/path` (not a resolvable link)
 * - Legacy space link:     `/feed` (bare path; treated as raw until re-selected)
 * - Empty:                 `` (not a resolvable link)
 *
 * ## Usage
 *
 * When a picker selects a space, persist `buildLink({ type: 'space', spaceId,
 * fallback })` (see `./build-link.ts`) — never the bare `relativeUrl`. To read a
 * stored value, call `resolveLink` and act on the returned shape: a
 * `ResolveLink` object for a recognized token, or the original `string` for
 * anything else. Rendering resolves the live path automatically —
 * `react-components/Link` swaps the token for the space's current `relativeUrl`
 * (falling back to the embedded path on first render or when the space is
 * unresolved). See `docs/conventions/development-patterns.md` ("Space links in
 * slates and pickers").
 */

/** Public taxonomy of resolvable-link kinds. Extend as more entities gain tokens. */
export type ResolveLinkType = 'space' | 'unknown'

/** A parsed space reference: the stable id plus the fallback path captured at build time. */
export type SpaceLink = {
  type: 'space'
  spaceId: string
  /** Path captured at build time; used when the live path can't be resolved. May be empty. */
  fallback: string
}

/** Structured result of {@link resolveLink}. A discriminating union — only `space` today. */
export type ResolveLink = SpaceLink

/** The space-link scheme. Internal to this module + `./build-link.ts`; not barrel-exported. */
export const SPACE_LINK_PREFIX = 'space://'

/**
 * Internal classifier by scheme. Detects the `space://` prefix and strips it —
 * the branded `space` value is the protocol-less remainder (`<id>:<path>`) that
 * {@link resolveSpaceLink} consumes directly; `unknown` carries the original
 * value unchanged. Validity (a non-empty id) is checked once by
 * {@link resolveLink}, not here.
 *
 * Exported for unit tests; not re-exported from the package barrel.
 */
export const parseLink = (
  value: string,
): { type: 'space'; value: string } | { type: 'unknown'; value: string } =>
  value.startsWith(SPACE_LINK_PREFIX)
    ? { type: 'space', value: value.slice(SPACE_LINK_PREFIX.length) }
    : { type: 'unknown', value }

/**
 * Internal, total: turns a branded space value (the protocol-less remainder
 * produced by {@link parseLink}) into a structured {@link SpaceLink}. Splits on
 * the first `:`; the remainder is the fallback path (may be empty or contain
 * further `:`/`/`). An empty remainder before the colon yields an empty
 * `spaceId`, which {@link resolveLink} treats as malformed.
 *
 * Deliberately does NOT use `new URL()`: the WHATWG URL parser lowercases the
 * authority, which would corrupt case-sensitive nanoid space ids.
 *
 * Exported for unit tests; not re-exported from the package barrel.
 */
export const resolveSpaceLink = (link: {
  type: 'space'
  value: string
}): SpaceLink => {
  const colonIndex = link.value.indexOf(':')
  const spaceId =
    colonIndex === -1 ? link.value : link.value.slice(0, colonIndex)
  const fallback = colonIndex === -1 ? '' : link.value.slice(colonIndex + 1)

  return { type: 'space', spaceId, fallback }
}

/**
 * Resolves a stored link value into its structured form. Returns a
 * {@link ResolveLink} for a recognized token (today only `space://…`), or the
 * original `string` for a raw URL/path or a malformed token (e.g. a `space://`
 * scheme with an empty id). Callers narrow on the return: `typeof result ===
 * 'string'` is a raw value, otherwise switch on `result.type`.
 *
 * Handle `undefined`/empty outside this function.
 */
export const resolveLink = (value: string): ResolveLink | string => {
  const parsed = parseLink(value)
  if (parsed.type !== 'space') {
    return parsed.value
  }

  const spaceLink = resolveSpaceLink(parsed)
  return spaceLink.spaceId ? spaceLink : value
}

/**
 * Type guard for any parsed resolvable-link token. Use it to narrow
 * `resolveLink`'s `ResolveLink | string` result — it is `true` for any
 * structured token (today only `SpaceLink`, more kinds later) and `false` for a
 * raw string. Callers that need to resolve a token but don't care about its kind
 * (e.g. `react-components/Link`) branch on this rather than on a specific kind.
 */
export const isResolveLink = (value: unknown): value is ResolveLink =>
  typeof value === 'object' && value !== null && 'type' in value

/**
 * Type guard for a resolved space link. Use it to narrow `resolveLink`'s
 * `ResolveLink | string` result — it is `true` only for a `SpaceLink` object,
 * never a raw string, and (once more kinds exist) never another `ResolveLink`
 * variant. Prefer this over a bare `typeof === 'object'` check.
 */
export const isResolveSpaceLink = (value: unknown): value is SpaceLink =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  value.type === 'space'
